/**
 * Hand rows to the pipeline directly.
 *
 * The connectors already know how to fetch from an app; this is the other half
 * — paste or upload what came back and watch it flow through. Re-sending the
 * same rows is safe, and the result says how many were duplicates rather than
 * pretending they were new.
 */

import { useState } from 'react'
import { Modal } from '@/shared/components'
import { useToast } from '@/shared/components/toast'
import { Field } from '../components'
import { useSourceMutations } from '../hooks'
import type { BrainSource } from '../types/brain.types'

export function IngestDialog({
  graphId,
  source,
  onClose,
}: {
  graphId: string
  source: BrainSource
  onClose: () => void
}) {
  const { ingest } = useSourceMutations(graphId)
  const toast = useToast()

  const [text, setText] = useState('')
  const [idField, setIdField] = useState('')
  const [error, setError] = useState<string | null>(null)

  function parse(): Record<string, unknown>[] | null {
    try {
      const parsed = JSON.parse(text)
      const rows = Array.isArray(parsed) ? parsed : [parsed]
      if (!rows.every((r) => r && typeof r === 'object')) {
        setError('Each row has to be an object.')
        return null
      }
      setError(null)
      return rows as Record<string, unknown>[]
    } catch (err) {
      setError((err as Error).message)
      return null
    }
  }

  return (
    <Modal
      title={`Ingest into ${source.display_name}`}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={ingest.isPending}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={ingest.isPending || !text.trim()}
            onClick={async () => {
              const rows = parse()
              if (!rows) return
              try {
                const result = await ingest.mutateAsync({
                  id: source.id,
                  records: rows,
                  id_field: idField.trim() || undefined,
                })
                toast.success(
                  `${result.staged} staged, ${result.duplicates} already seen.`,
                  'They are queued — press Run in the header to process them now.',
                )
                onClose()
              } catch (err) {
                toast.error(err)
              }
            }}
          >
            {ingest.isPending ? 'Sending…' : 'Ingest'}
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="muted small" style={{ margin: 0 }}>
          {source.mode === 'direct_map'
            ? `Rows are mapped straight onto ${source.entity_hint ?? 'a node type'} by matching column names to its properties.`
            : 'Each row is turned into text, chunked, and read by the extractor.'}
        </p>

        <Field label="Rows" required help="A JSON array of objects, or a single object.">
          <textarea
            className="textarea textarea-mono"
            rows={12}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder='[{"id": "1", "account_name": "Acme Corp", "domain": "acme.example"}]'
            aria-invalid={error ? 'true' : undefined}
          />
          {error && <p className="field-error">{error}</p>}
        </Field>

        <Field
          label="Which field is the row's id?"
          help="Used with the row's content to recognise a repeat. Left blank, 'id' or '_id' is used."
        >
          <input
            className="input"
            value={idField}
            onChange={(e) => setIdField(e.target.value)}
            placeholder="id"
          />
        </Field>
      </div>
    </Modal>
  )
}
